import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../components/button/button";
import { Checkbox } from "../../components/checkbox/checkbox";
import { Input } from "../../components/input/input";
import { Textarea } from "../../components/text-area/text-area";

export const formSchema = z
  .object({
    email: z.string().min(1, { message: "Email is required" }),
    name: z.string().optional(),
    telegram: z.string().optional(),
    message: z.string().optional(),
    terms: z.coerce.boolean().optional(),
    redirectToPlaybook: z.string().optional(),
    memberStatus: z.string().optional(),
    campaignId: z.string().optional(),
    utmCampaign: z.string().optional(),
    utmMedium: z.string().optional(),
    utmSource: z.string().optional(),
    organization: z.string().optional(),
    role: z.string().optional(),
  })
  .refine((data) => data.terms, {
    path: ["terms"],
    message: "You must agree to the privacy policy",
  });

export type FormData = z.infer<typeof formSchema>;

export type Props = {
  onSubmit: (data: FormData) => Promise<void>;
  buttonText: string;
  messagePrompt: string;
  defaultValues?: {
    redirectToPlaybook?: boolean;
    utmCampaign?: string;
    utmMedium?: string;
    utmSource?: string;
    memberStatus?: string;
    campaignId?: string;
  };
};

export const ContactUsForm = ({ onSubmit: onSubmitProp, buttonText, messagePrompt, defaultValues }: Props) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      redirectToPlaybook: defaultValues?.redirectToPlaybook ? "true" : "false",
      utmCampaign: defaultValues?.utmCampaign ?? undefined,
      utmMedium: defaultValues?.utmMedium ?? undefined,
      utmSource: defaultValues?.utmSource ?? undefined,
      memberStatus: defaultValues?.memberStatus ?? undefined,
      campaignId: defaultValues?.campaignId ?? undefined,
    },
  });

  const [isSuccess, setIsSuccess] = useState(false);

  const isTermsChecked = watch("terms");

  const onSubmit = async (data: FormData) => {
    await onSubmitProp(data);
    setIsSuccess(true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-lg lg:min-w-[450px] space-y-4">
      <div>
        <Label htmlFor="name">YOUR NAME</Label>
        <Input variant="marketing" id="name" placeholder="Annie Smith" {...register("name")} />
        {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
      </div>
      <div className="flex flex-col md:flex-row w-full gap-4 justify-evenly">
        <div className="w-full">
          <Label htmlFor="email">WORK EMAIL</Label>
          <Input variant="marketing" id="email" placeholder="annie@acme.xyz" {...register("email")} />
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
        </div>
        <div className="w-full">
          <Label htmlFor="telegram">TELEGRAM USERNAME</Label>
          <Input variant="marketing" id="telegram" placeholder="@annie_xyz" {...register("telegram")} />
          {errors.telegram && <ErrorMessage>{errors.telegram.message}</ErrorMessage>}
        </div>
      </div>

      <div className="flex flex-col md:flex-row w-full gap-4 justify-evenly">
        <div className="w-full">
          <Label htmlFor="organization">ORGANIZATION</Label>
          <Input variant="marketing" id="organization" placeholder="Acme xyz" {...register("organization")} />
          {errors.organization && <ErrorMessage>{errors.organization.message}</ErrorMessage>}
        </div>
        <div className="w-full">
          <Label htmlFor="role">ROLE</Label>
          <Input variant="marketing" id="role" placeholder="Growth strategist" {...register("role")} />
          {errors.role && <ErrorMessage>{errors.role.message}</ErrorMessage>}
        </div>
      </div>

      <div>
        <Label htmlFor="message">{messagePrompt}</Label>
        <Textarea className="min-h-36" placeholder="I'd like to learn more about..." {...register("message")} />
        {errors.message && <ErrorMessage>{errors.message.message}</ErrorMessage>}
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={isTermsChecked}
          onCheckedChange={(checked) => {
            setValue("terms", !!checked);
          }}
        />
        <Label htmlFor="terms">
          <span
            className="cursor-pointer font-aeonikMono"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setValue("terms", !isTermsChecked);
            }}
          >
            I AGREE TO THE{" "}
          </span>
          <a
            href="https://flipsidecrypto.xyz/privacy"
            target="_blank"
            className="underline font-aeonikMono"
            rel="noreferrer"
          >
            PRIVACY POLICY
          </a>
        </Label>
      </div>
      {errors.terms && <ErrorMessage>{errors.terms.message}</ErrorMessage>}
      <Button disabled={!isTermsChecked || isSuccess} type="submit" font="mono">
        {buttonText}
      </Button>
      {isSuccess && !defaultValues?.redirectToPlaybook && (
        <p className="text-text-success text-sm mt-1">Thank you for your message</p>
      )}
    </form>
  );
};

const Label = ({ children, htmlFor }: { children: React.ReactNode; htmlFor: string }) => {
  return (
    <label className="text-text-secondary text-sm font-aeonikMono uppercase" htmlFor={htmlFor}>
      {children}
    </label>
  );
};

const ErrorMessage = ({ children }: { children: React.ReactNode }) => {
  return <p className="text-text-error text-sm mt-1">{children}</p>;
};
